import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  IoStopCircleOutline,
  IoPlayCircleOutline,
  IoShareSocialOutline,
  IoCloseOutline,
  IoLogoYoutube,
} from 'react-icons/io5';
import { PuffLoader } from 'react-spinners';
import fallbackCover from '../../assets/images/logo512.png';
import SocialIcons from '../SocialIcons/SocialIcons';
import { API } from '../../api';

import './NowPlaying.css';
import axios from 'axios';
import { API_DEEZER_SEARCH } from '../../api';

const MAX_HISTORY = 5;

const NowPlaying = (props) => {
  const [image, setImage] = useState(null);
  const [history, setHistory] = useState([]);
  const [shareCopied, setShareCopied] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  // Saugo yp_currently_playing + pavadinimai + cover prieš pereina į kitą dainą
  const prevSongRef = useRef(null); // { artist, title, yp }
  const prevImageRef = useRef(null);

  /* ---------- PRADINĖ ISTORIJA IŠ API ---------- */
  useEffect(() => {
    axios
      .get(API)
      .then((res) => {
        const songs = res.data.song_history;
        if (!Array.isArray(songs)) return;
        setHistory(
          songs.slice(0, MAX_HISTORY).map((item) => ({
            artist: item.song.artist || 'Nežinoma',
            title: item.song.title || 'Nežinoma',
            image: item.song.art || fallbackCover,
            time: new Date(item.played_at * 1000),
          })),
        );
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------- DINAMINIS NARŠYKLĖS TAB PAVADINIMAS ---------- */
  useEffect(() => {
    if (props.data.artist && props.data.title) {
      document.title = `${props.data.artist} \u2013 ${props.data.title} | BEVEIK RADIJAS`;
    }
  }, [props.data.artist, props.data.title]);

  /* ---------- ESCAPE KLAVIŠAS MODALUI ---------- */
  useEffect(() => {
    if (!showHistoryModal) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setShowHistoryModal(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showHistoryModal]);

  /* ---------- COVER ART + METADUOMENYS + ISTORIJA ---------- */
  useEffect(() => {
    if (!props.data.yp_currently_playing) return;

    const fetchAndSetMetadata = async () => {
      // Išsaugome į istoriją TIK jei daina tikrai pasikeitė
      // (apsaugoja nuo React StrictMode dvigubo paleidimo ir race condition)
      if (prevSongRef.current !== null && prevSongRef.current.yp !== props.data.yp_currently_playing) {
        const prev = prevSongRef.current;
        if (prev.artist || prev.title) {
          setHistory((h) =>
            [
              {
                artist: prev.artist || 'Nežinoma',
                title: prev.title || 'Nežinoma',
                image: prevImageRef.current || fallbackCover,
                time: new Date(),
              },
              ...h,
            ].slice(0, MAX_HISTORY),
          );
        }
        setImage(null); // atstatome cover kol kraunama nauja
      }

      prevSongRef.current = {
        artist: props.data.artist,
        title: props.data.title,
        yp: props.data.yp_currently_playing,
      };

      try {
        const res = await axios.get(`${API_DEEZER_SEARCH}?q=${props.data.yp_currently_playing}`);
        const result = res.data.data[0];
        const cover = result?.album?.cover_big || fallbackCover;

        const getMimeType = (filename) => {
          const ext = filename.split('.').pop().toLowerCase();
          if (ext === 'png') return 'image/png';
          return 'image/jpeg';
        };

        if ('mediaSession' in navigator) {
          navigator.mediaSession.metadata = new MediaMetadata({
            title: props.data.title || result?.title || 'Unknown Title',
            artist: props.data.artist || result?.artist?.name || 'Unknown Artist',
            artwork: [{ src: cover, sizes: '512x512', type: getMimeType(cover) }],
          });
          navigator.mediaSession.setActionHandler('play', () => props.playStopBtn(true));
          navigator.mediaSession.setActionHandler('pause', () => props.playStopBtn(false));
        }

        prevImageRef.current = cover;
        setImage(cover);
      } catch (err) {
        console.error('Klaida gaunant viršelį iš Deezer:', err);
        prevImageRef.current = fallbackCover;
        setImage(fallbackCover);
      }
    };

    fetchAndSetMetadata();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.data.yp_currently_playing]);

  /* ---------- SHARE ---------- */
  const handleShare = useCallback(async () => {
    const text = `${props.data.artist} \u2013 ${props.data.title}`;
    // share.php grąžina og:image su dainos cover art'u socialinių tinklų crawleriams,
    // o tikrus vartotojus iš karto nukreipia atgal į beveikradijas.com.
    // Timestamp užtikrina, kad Facebook/kiti nekešuotų seno post'o.
    const shareUrl = `https://beveikradijas.com/share.php?t=${Date.now()}`;
    const url = window.location.href;
    const shareText = `\uD83C\uDFB5 Klausausi: ${text}`;

    // Bandome gauti cover art kaip blob (tik jei tai HTTP nuoroda, ne lokalus fallback)
    let imageBlob = null;
    if (image && typeof image === 'string' && image.startsWith('http')) {
      try {
        const res = await fetch(image, { mode: 'cors' });
        if (res.ok) imageBlob = await res.blob();
      } catch {
        // Tęsiame be paveiksliuko
      }
    }

    // Sukuria share card: cover art + teksto overlayus viename PNG
    const toShareCardBlob = (blob) =>
      new Promise((resolve) => {
        const img = new Image();
        const blobUrl = URL.createObjectURL(blob);
        img.onload = () => {
          const SIZE = 600;
          const FOOTER = 110;
          const canvas = document.createElement('canvas');
          canvas.width = SIZE;
          canvas.height = SIZE + FOOTER;
          const ctx = canvas.getContext('2d');

          // Cover art (kvadratinis)
          ctx.drawImage(img, 0, 0, SIZE, SIZE);

          // Tamsus gradientas per apačią cover art'o (sklandus perėjimas į footer)
          const grad = ctx.createLinearGradient(0, SIZE - 80, 0, SIZE);
          grad.addColorStop(0, 'rgba(0,0,0,0)');
          grad.addColorStop(1, 'rgba(0,0,0,0.85)');
          ctx.fillStyle = grad;
          ctx.fillRect(0, SIZE - 80, SIZE, 80);

          // Footer juoda juosta
          ctx.fillStyle = '#111111';
          ctx.fillRect(0, SIZE, SIZE, FOOTER);

          // Artistas
          ctx.fillStyle = '#ffffff';
          ctx.font = 'bold 22px system-ui, sans-serif';
          ctx.textBaseline = 'top';
          ctx.fillText(props.data.artist || '', 20, SIZE + 14, SIZE - 40);

          // Dainos pavadinimas
          ctx.fillStyle = '#cccccc';
          ctx.font = '18px system-ui, sans-serif';
          ctx.fillText(props.data.title || '', 20, SIZE + 44, SIZE - 40);

          // BEVEIK RADIJAS + URL
          ctx.fillStyle = '#888888';
          ctx.font = '13px system-ui, sans-serif';
          ctx.fillText(`BEVEIK RADIJAS \u2013 ${url}`, 20, SIZE + 78, SIZE - 40);

          URL.revokeObjectURL(blobUrl);
          canvas.toBlob(resolve, 'image/png');
        };
        img.onerror = () => {
          URL.revokeObjectURL(blobUrl);
          resolve(null);
        };
        img.src = blobUrl;
      });

    const copyFallback = async () => {
      try {
        // Desktop: bandome nukopijuoti paveiksliuką į iškarpinę
        if (imageBlob && typeof window.ClipboardItem !== 'undefined') {
          const cardBlob = await toShareCardBlob(imageBlob);
          if (cardBlob) {
            const textBlob = new Blob([`${shareText} | BEVEIK RADIJAS\n${shareUrl}`], { type: 'text/plain' });
            await navigator.clipboard.write([
              new window.ClipboardItem({ 'image/png': cardBlob, 'text/plain': textBlob }),
            ]);
            setShareCopied(true);
            setTimeout(() => setShareCopied(false), 2000);
            return;
          }
        }
        // Fallback: tekstas + URL
        await navigator.clipboard.writeText(`${shareText} | BEVEIK RADIJAS\n${shareUrl}`);
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 2000);
      } catch {
        window.prompt('Nukopijuokite nuorodą:', shareUrl);
      }
    };

    if (navigator.share && window.isSecureContext) {
      try {
        // Mobilus: share su cover art failu jei platforma palaiko
        if (imageBlob && navigator.canShare) {
          const cardBlob = await toShareCardBlob(imageBlob);
          const shareFile = cardBlob ? new File([cardBlob], 'beveik-radijas.png', { type: 'image/png' }) : null;
          if (shareFile && navigator.canShare({ files: [shareFile] })) {
            await navigator.share({
              files: [shareFile],
              title: `BEVEIK RADIJAS \u2013 ${text}`,
              text: shareText,
              url: shareUrl,
            });
          } else {
            await navigator.share({ title: `BEVEIK RADIJAS \u2013 ${text}`, text: shareText, url: shareUrl });
          }
        } else {
          await navigator.share({ title: `BEVEIK RADIJAS \u2013 ${text}`, text: shareText, url: shareUrl });
        }
      } catch (err) {
        if (err.name !== 'AbortError') await copyFallback();
      }
    } else {
      await copyFallback();
    }

    // Mobiliuoju sistema gali pristabdyti audio atidarant share lapą — atnaujinamas
    if (props.isPlaying && props.resumeAudio) {
      props.resumeAudio();
    }
  }, [image, props.data.artist, props.data.title, props.isPlaying, props.resumeAudio]);

  const formatTime = (date) => date.toLocaleTimeString('lt-LT', { hour: '2-digit', minute: '2-digit' });

  const volumePct = Math.round((props.isMuted ? 0 : props.volume) * 100);

  return (
    <section className='main-containe__player' aria-label='Radijo grotuvas'>
      <div className='main-containe__player-image-container'>
        {image ? (
          <img
            className='main-containe__player-image-container__artwork'
            src={image}
            alt={
              props.data.title ? `${props.data.artist} \u2013 ${props.data.title} albumo viršelis` : 'Albumo viršelis'
            }
          />
        ) : (
          <PuffLoader color={'#fff'} size={60} aria-label='Kraunasi albumo viršelis' />
        )}
        <canvas id='canvas' ref={props.canvasRef} aria-hidden='true' />
        <button
          className='main-containe__player-image-container__btn'
          onClick={() => props.playStopBtn(!props.isPlaying)}
          type='button'
          aria-label={props.isPlaying ? 'Stabdyti transliaciją' : 'Leisti transliaciją'}
          aria-pressed={props.isPlaying}
        >
          {props.isPlaying ? (
            <IoStopCircleOutline size={80} color={'#fff'} aria-hidden='true' />
          ) : (
            <IoPlayCircleOutline size={80} color={'#fff'} aria-hidden='true' />
          )}
        </button>
      </div>

      <div className='main-containe__player-now-playing-container'>
        <h3 className='main-containe__player-now-playing-container__text'>Eteryje:</h3>
        <div aria-live='polite' aria-atomic='true'>
          <h4 className='main-containe__player-now-playing-container__artist'>{props.data.artist}</h4>
          <h5 className='main-containe__player-now-playing-container__title'>{props.data.title}</h5>
        </div>

        {/* Garsumo valdiklis */}
        <div className='player-volume' role='group' aria-label='Garsumo valdiklis'>
          <button
            className='player-volume__mute-btn'
            onClick={() => props.setIsMuted(!props.isMuted)}
            aria-label={props.isMuted ? 'Įjungti garsą' : 'Nutildyti'}
            aria-pressed={props.isMuted}
          >
            {props.isMuted || props.volume === 0 ? '🔇' : props.volume < 0.5 ? '🔉' : '🔊'}
          </button>
          <input
            className='player-volume__slider'
            type='range'
            min='0'
            max='1'
            step='0.05'
            value={props.isMuted ? 0 : props.volume}
            onChange={(e) => {
              const val = parseFloat(e.target.value);
              props.setVolume(val);
              if (props.isMuted && val > 0) props.setIsMuted(false);
            }}
            style={{
              background: `linear-gradient(to right, #ff6f61 ${volumePct}%, #3a3a45 ${volumePct}%)`,
            }}
            aria-label='Garsumas'
            aria-valuemin='0'
            aria-valuemax='100'
            aria-valuenow={volumePct}
          />
          <span className='player-volume__label' aria-hidden='true'>
            {volumePct}%
          </span>
        </div>

        {/* Share mygtukas */}
        <button className='player-share-btn' onClick={handleShare} aria-label='Dalintis šiuo metu skambančia daina'>
          <IoShareSocialOutline size={18} aria-hidden='true' />
          <span>{shareCopied ? 'Nukopijuota!' : 'Dalintis'}</span>
        </button>
      </div>

      <div className='main-containe__player-next-playing-container'>
        {props.width > 1130 ? <SocialIcons width={props.width} /> : <></>}
      </div>

      {/* Ankstesnė daina: rodomas 1 įrašas + mygtukas modalui */}
      {history.length > 0 && (
        <div className='player-history-preview'>
          <span className='player-history-preview__label'>Skambėjo anksčiau:</span>
          <button
            className='player-history-preview__btn'
            onClick={() => setShowHistoryModal(true)}
            aria-label={`Paskutinė: ${history[0].artist} \u2013 ${history[0].title}. Spustelėkite peržiūrėti visą istoriją.`}
          >
            <img
              className='player-history-preview__cover'
              src={history[0].image}
              alt={`${history[0].artist} \u2013 ${history[0].title}`}
            />
            <div className='player-history-preview__info'>
              <span className='player-history-preview__artist'>{history[0].artist}</span>
              <span className='player-history-preview__song'>{history[0].title}</span>
            </div>
            <time className='player-history-preview__time' dateTime={history[0].time.toISOString()}>
              {formatTime(history[0].time)}
            </time>
            {history.length > 1 && <span className='player-history-preview__more'>+{history.length - 1}</span>}
          </button>
        </div>
      )}

      {/* Dainų istorijos modalas */}
      {showHistoryModal && (
        <div
          className='history-modal__overlay'
          onClick={() => setShowHistoryModal(false)}
          role='dialog'
          aria-modal='true'
          aria-label='Dainų istorija'
        >
          <div className='history-modal' onClick={(e) => e.stopPropagation()}>
            <div className='history-modal__header'>
              <h3 className='history-modal__title'>Skambėjo anksčiau</h3>
              <button className='history-modal__close' onClick={() => setShowHistoryModal(false)} aria-label='Uždaryti'>
                <IoCloseOutline size={24} aria-hidden='true' />
              </button>
            </div>
            <ul className='history-modal__list'>
              {history.map((song, i) => (
                <li key={i} className='history-modal__item'>
                  <img
                    className='history-modal__cover'
                    src={song.image}
                    alt={`${song.artist} \u2013 ${song.title} albumo viršelis`}
                  />
                  <div className='history-modal__info'>
                    <span className='history-modal__artist'>{song.artist}</span>
                    <span className='history-modal__song'>{song.title}</span>
                  </div>
                  <a
                    className='history-modal__yt'
                    href={`https://www.youtube.com/results?search_query=${encodeURIComponent(`${song.artist} ${song.title}`)}`}
                    target='_blank'
                    rel='noopener noreferrer'
                    aria-label={`Ieškoti "${song.artist} \u2013 ${song.title}" YouTube`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <IoLogoYoutube size={22} aria-hidden='true' />
                  </a>
                  <time className='history-modal__time' dateTime={song.time.toISOString()}>
                    {formatTime(song.time)}
                  </time>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </section>
  );
};

export default NowPlaying;
